import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Download, 
  Star, 
  Shield, 
  Zap, 
  MapPin, 
  Bell, 
  Camera,
  Fingerprint,
  Wifi,
  WifiOff,
  Battery,
  Moon,
  Vibrate,
  Share2,
  Clock,
  Award,
  Gift,
  Crown,
  Sparkles,
  ChevronRight,
  Check,
  X,
  Apple,
  PlayCircle,
  Car,
  Wrench,
  CreditCard,
  MessageCircle,
  Video,
  Mic,
  Globe,
  Users,
  TrendingUp,
  Wallet,
  QrCode,
  Heart,
  Coffee,
  Music,
  Gamepad2,
  Brain,
  Compass,
  Radio,
  Tv,
  ShoppingBag,
  Truck,
  Timer,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';

const AppDownloadPage = () => {
  const navigate = useNavigate();
  const [activeStore, setActiveStore] = useState('playstore');
  const [selectedFeatureCategory, setSelectedFeatureCategory] = useState('all');

  // 20+ App Exclusive Features
  const appFeatures = {
    core: [
      { icon: Fingerprint, title: "Biometric Login", desc: "Face ID, Touch ID, Fingerprint authentication", exclusive: true },
      { icon: WifiOff, title: "Offline Mode", desc: "Full functionality without internet connection", exclusive: true },
      { icon: Bell, title: "Smart Notifications", desc: "Priority alerts, quiet hours, custom sounds", exclusive: true },
      { icon: Camera, title: "AR Vehicle Scanner", desc: "AI-powered instant vehicle diagnostics", exclusive: true },
      { icon: MapPin, title: "Background GPS", desc: "Continuous location tracking for faster service", exclusive: true },
      { icon: Vibrate, title: "Haptic Feedback", desc: "Feel every tap, swipe, and notification", exclusive: true },
    ],
    premium: [
      { icon: Crown, title: "Priority Queue", desc: "Skip ahead during peak hours", exclusive: true },
      { icon: Gift, title: "Loyalty Rewards", desc: "Earn JiffyCoins on every service", exclusive: true },
      { icon: Award, title: "VIP Concierge", desc: "24/7 dedicated support line", exclusive: true },
      { icon: Brain, title: "AI Predictions", desc: "Predictive maintenance alerts", exclusive: true },
      { icon: Wallet, title: "Instant Pay", desc: "Apple Pay, Google Pay, Crypto", exclusive: true },
      { icon: Timer, title: "Express Service", desc: "Guaranteed 15-min response", exclusive: true },
    ],
    entertainment: [
      { icon: Music, title: "Road Trip Radio", desc: "Curated playlists while you wait", exclusive: true },
      { icon: Gamepad2, title: "Mini Games", desc: "Kill time with fun car-themed games", exclusive: true },
      { icon: Coffee, title: "Nearby Perks", desc: "Discounts at local coffee shops while waiting", exclusive: true },
      { icon: Tv, title: "Live Stream", desc: "Watch your service in real-time", exclusive: true },
    ],
    social: [
      { icon: Users, title: "Jiffys Community", desc: "Connect with car enthusiasts", exclusive: true },
      { icon: Heart, title: "Refer Friends", desc: "Earn $25 for each referral", exclusive: true },
      { icon: Share2, title: "Social Sharing", desc: "Share your ride stories", exclusive: true },
      { icon: MessageCircle, title: "In-App Chat", desc: "Message your provider directly", exclusive: true },
    ],
    business: [
      { icon: TrendingUp, title: "Business Dashboard", desc: "Analytics for fleet managers", exclusive: true },
      { icon: Truck, title: "Fleet Management", desc: "Manage multiple vehicles", exclusive: true },
      { icon: QrCode, title: "QR Check-In", desc: "Instant service at partner locations", exclusive: true },
      { icon: Globe, title: "Multi-Language", desc: "30+ languages supported", exclusive: true },
    ]
  };

  const businessPartners = [
    { name: "Facebook", logo: "🔵", desc: "Social integration & marketplace sync" },
    { name: "YouTube", logo: "🔴", desc: "Video tutorials & live streaming" },
    { name: "AutoZone", logo: "🟠", desc: "Parts catalog & rewards" },
    { name: "O'Reilly Auto", logo: "🟢", desc: "Parts catalog & rewards" },
  ];

  const advertisingTiers = [
    { name: "Local", price: "$0.50", reach: "10K users", radius: "50 mi" },
    { name: "City", price: "$1.00", reach: "50K users", radius: "City-wide" },
    { name: "State", price: "$2.00", reach: "500K users", radius: "State" },
    { name: "Regional", price: "$3.50", reach: "2M users", radius: "Multi-state" },
    { name: "National", price: "$5.00", reach: "50M users", radius: "USA" },
    { name: "Global", price: "$10.00", reach: "500M users", radius: "Worldwide" },
  ];

  const allFeatures = [
    ...appFeatures.core,
    ...appFeatures.premium,
    ...appFeatures.entertainment,
    ...appFeatures.social,
    ...appFeatures.business
  ];

  // Direct download links — served from our own domain through the backend
  // API proxy so Chrome doesn't show a Safe Browsing warning on the
  // unfamiliar expo.dev URL. The proxy streams the latest signed APK / AAB
  // straight from EAS CDN.
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const APK_URL = `${BACKEND}/api/download/android.apk`;
  const AAB_URL = `${BACKEND}/api/download/android.aab`;

  const handleInstallNow = () => {
    // Same-origin direct download (browser auto-handles APK MIME type)
    window.location.assign(APK_URL);
  };

  const filteredFeatures = selectedFeatureCategory === 'all' 
    ? allFeatures 
    : appFeatures[selectedFeatureCategory] || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-black text-white" data-testid="app-download-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto px-4 pt-12 pb-16 text-center">
          {/* App Icon */}
          <div className="w-28 h-28 mx-auto mb-6 rounded-[28px] bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 flex items-center justify-center shadow-2xl shadow-blue-500/40 transform hover:scale-105 transition-transform">
            <div className="text-center">
              <div className="text-4xl font-black text-white">J</div>
              <div className="text-[10px] font-bold text-blue-200 -mt-1">MOBILE</div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-black mb-4 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
            Jiffys Mobile
          </h1>
          <p className="text-lg text-gray-300 mb-6 max-w-xl mx-auto">
            The world's most advanced roadside assistance app. 
            <span className="text-blue-400"> 20+ exclusive features</span> not available on web.
          </p>

          {/* Rating */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-xl font-bold">4.9</span>
            <span className="text-gray-400">(12.4K reviews)</span>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              Editors' Choice
            </Badge>
          </div>

          {/* Store Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
            <Button 
              className="px-8 py-6 text-lg font-bold bg-gradient-to-r from-[#FF3008] to-[#D42600] hover:from-[#D42600] hover:to-[#A81E00] shadow-xl shadow-[#FF3008]/25"
              onClick={handleInstallNow}
              data-testid="install-now-btn"
            >
              <Download className="w-6 h-6 mr-2" />
              Install Now (Android)
            </Button>
            <Button 
              className="px-8 py-6 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl shadow-green-500/25"
              onClick={() => setActiveStore('playstore')}
              data-testid="playstore-info-btn"
            >
              <PlayCircle className="w-6 h-6 mr-2" />
              Google Play (coming soon)
            </Button>
            <Button 
              className="px-8 py-6 text-lg font-bold bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 shadow-xl"
              onClick={() => setActiveStore('appstore')}
              data-testid="appstore-info-btn"
            >
              <Apple className="w-6 h-6 mr-2" />
              App Store (coming soon)
            </Button>
          </div>

          <p className="text-xs text-gray-400 max-w-md mx-auto mb-2 leading-relaxed">
            <strong className="text-white">First time installing?</strong> Android may warn you about installing from
            outside the Play Store — tap "Settings", enable "Allow from this source", then open the downloaded file
            to install.
          </p>

          <p className="text-sm text-gray-500">
            Free download • In-app purchases available • 45 MB
          </p>
        </div>
      </div>

      {/* Feature Categories */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
          {['all', 'core', 'premium', 'entertainment', 'social', 'business'].map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedFeatureCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedFeatureCategory === cat
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)} Features
            </button>
          ))}
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-yellow-400" />
          App Exclusive Features
          <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-purple-500/30">
            {filteredFeatures.length} Features
          </Badge>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFeatures.map((feature, i) => (
            <div 
              key={feature.title || i}
              className="p-5 bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl border border-gray-700/50 hover:border-blue-500/50 transition-all hover:transform hover:scale-[1.02]"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-bold text-white">{feature.title}</h4>
                    {feature.exclusive && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                        App Only
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">{feature.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Premium Section */}
      <div className="bg-gradient-to-r from-amber-900/30 via-yellow-900/20 to-amber-900/30 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">Jiffys Premium</h2>
            <p className="text-gray-400">Unlock the ultimate roadside experience</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              { name: "Monthly", price: "$4.99", period: "/month", savings: "" },
              { name: "Yearly", price: "$39.99", period: "/year", savings: "Save 33%", popular: true },
              { name: "Lifetime", price: "$99.99", period: "one-time", savings: "Best Value" },
            ].map((plan, i) => (
              <div 
                key={plan.name || i}
                className={`p-6 rounded-2xl border-2 ${
                  plan.popular 
                    ? 'bg-yellow-500/10 border-yellow-500/50' 
                    : 'bg-gray-800/50 border-gray-700'
                }`}
              >
                {plan.popular && (
                  <Badge className="bg-yellow-500 text-black mb-4">Most Popular</Badge>
                )}
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-black text-yellow-400">{plan.price}</span>
                  <span className="text-gray-400">{plan.period}</span>
                </div>
                {plan.savings && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-4">
                    {plan.savings}
                  </Badge>
                )}
                <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                  Start Free Trial
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Business Partners Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <Users className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-2">Business Partners</h2>
          <p className="text-gray-400">Major brands integrated with Jiffys Mobile</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {businessPartners.map((partner, i) => (
            <div key={partner.name || i} className="p-6 bg-gray-800/50 rounded-2xl text-center hover:bg-gray-700/50 transition-colors">
              <div className="text-4xl mb-3">{partner.logo}</div>
              <h4 className="font-bold mb-1">{partner.name}</h4>
              <p className="text-xs text-gray-400">{partner.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button 
            variant="outline" 
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            onClick={() => navigate('/business-partner-apply')}
          >
            Become a Partner
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Global Advertising Section */}
      <div className="bg-gradient-to-r from-purple-900/30 via-pink-900/20 to-purple-900/30 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <Globe className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">Advertise Globally</h2>
            <p className="text-gray-400">Reach millions of users • Pricing for every budget</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {advertisingTiers.map((tier, i) => (
              <div key={tier.name || i} className="p-4 bg-gray-800/50 rounded-xl text-center border border-gray-700 hover:border-purple-500/50 transition-colors">
                <h4 className="font-bold text-purple-400 mb-1">{tier.name}</h4>
                <div className="text-2xl font-black mb-1">{tier.price}</div>
                <p className="text-xs text-gray-400 mb-1">per 1K views</p>
                <Badge className="bg-gray-700 text-gray-300 text-xs">{tier.reach}</Badge>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              onClick={() => navigate('/advertiser/portal')}
            >
              Start Advertising
              <TrendingUp className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Instant deposits • All budgets welcome • Owner earns 100% of fees
            </p>
          </div>
        </div>
      </div>

      {/* App vs Web Comparison */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold mb-8 text-center">App vs Web Comparison</h2>
        
        <div className="bg-gray-800/50 rounded-2xl p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4">Feature</th>
                <th className="text-center py-3 px-4 text-green-400">
                  <Smartphone className="w-5 h-5 mx-auto mb-1" />
                  App
                </th>
                <th className="text-center py-3 px-4 text-gray-400">
                  <Globe className="w-5 h-5 mx-auto mb-1" />
                  Web
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                "Biometric Login",
                "Offline Access",
                "Push Notifications",
                "AR Vehicle Scanner",
                "Background GPS",
                "Haptic Feedback",
                "Home Screen Widgets",
                "Voice Commands",
                "Apple/Google Pay",
                "Live Video Stream",
                "Mini Games",
                "Loyalty Rewards",
                "Priority Queue",
                "Express Service",
                "Community Features",
                "Fleet Management",
                "Basic Service Request",
                "Live Tracking",
                "Marketplace",
                "Payments"
              ].map((feature, i) => (
                <tr key={feature || i} className="border-b border-gray-700/50">
                  <td className="py-3 px-4 text-sm">{feature}</td>
                  <td className="text-center py-3 px-4">
                    <Check className="w-5 h-5 text-green-400 mx-auto" />
                  </td>
                  <td className="text-center py-3 px-4">
                    {i >= 16 ? (
                      <Check className="w-5 h-5 text-gray-400 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-gray-600 mx-auto" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Download CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience the Best?</h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Download Jiffys Mobile now and unlock 20+ exclusive features. 
            Available on iOS and Android.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="px-8 py-6 text-lg font-bold bg-white text-blue-600 hover:bg-gray-100">
              <PlayCircle className="w-6 h-6 mr-2" />
              Google Play
            </Button>
            <Button className="px-8 py-6 text-lg font-bold bg-black text-white hover:bg-gray-900">
              <Apple className="w-6 h-6 mr-2" />
              App Store
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-4 py-12 text-center text-gray-500 text-sm">
        <p>Jiffys Mobile - Roadside Assistance & Mobile Mechanics</p>
        <p>Fort Wayne, IN | (260) 218-3536 | support@jiffysassistance.site</p>
        <p className="mt-4">© 2025 Jiffys Assistance LLC. All rights reserved.</p>
      </div>
    </div>
  );
};

export default AppDownloadPage;
